import React, { useState, useEffect, useRef, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import { useSearchParams, useParams } from 'react-router-dom';
import { createPublicApi } from '../services/api';
import { useCart } from '../context/CartContext';
import MenuItemCard from '../components/menu/MenuItemCard';
import { SkeletonCard } from '../components/common/Skeleton';
import ReceiptGenerator from '../components/common/ReceiptGenerator';

const PLACEHOLDER = 'https://images.unsplash.com/photo-1509042239860-f550ce710b93?w=80&q=80';
const BASE = process.env.REACT_APP_API_URL?.replace('/api','') || 'http://localhost:5000';

const C = {
  accent:'#c96428', accentL:'#fff4ee', accentB:'#f0d0b0',
  text1:'#1a0f00', text3:'#6b4c35', text4:'#a07858',
  border:'#f0e4d8', border2:'#f5ede4', card:'#ffffff', surface:'#fffaf7',
  green:'#16a34a', greenL:'#dcfce7',
};

const StepBadge = ({ num, label, active, done, onClick }) => (
  <button onClick={onClick} style={{
    display:'flex', alignItems:'center', gap:6,
    padding:'7px 14px', borderRadius:99,
    border:`1.5px solid ${active?C.accent:done?'#a5d6a7':C.border}`,
    background:active?C.accent:done?C.greenL:C.card,
    cursor:'pointer', transition:'all 0.2s', whiteSpace:'nowrap',
  }}>
    <div style={{
      width:20, height:20, borderRadius:'50%',
      background:active?'rgba(255,255,255,0.25)':done?C.green:'#f0e4d8',
      display:'flex', alignItems:'center', justifyContent:'center',
      fontSize:10, fontWeight:800,
      color:active?'#fff':done?'#fff':C.text4,
      flexShrink:0,
    }}>{done?'✓':num}</div>
    <span style={{ fontSize:13, fontWeight:700, color:active?'#fff':done?C.green:C.text3 }}>{label}</span>
  </button>
);

const Order = () => {
  const { slug } = useParams();
  const { items, total, count, updateQty, removeItem, clearCart } = useCart();
  const [searchParams] = useSearchParams();
  const tableFromUrl   = searchParams.get('table') || '';

  const [menuItems,     setMenuItems]     = useState([]);
  const [categories,    setCategories]    = useState([]);
  const [activeCategory,setActiveCategory]= useState('');
  const [loading,       setLoading]       = useState(true);
  const [step,          setStep]          = useState('menu');
  const [form,          setForm]          = useState({
    customer_name:'', phone:'', email:'',
    order_type: tableFromUrl ? 'dine-in' : 'dine-in',
    address:'', notes:'', table_number: tableFromUrl,
  });
  const [submitting,    setSubmitting]    = useState(false);
  const [orderId,       setOrderId]       = useState(null);
  const [gstSettings,   setGstSettings]   = useState({ gst_enabled:'false', gst_rate:'0', gst_label:'GST' });
  const [deliveryEnabled, setDeliveryEnabled] = useState(false);
  const [deliveryCharge, setDeliveryCharge] = useState(0);
  const [placedOrder,   setPlacedOrder]   = useState(null);
  const [showReceipt,   setShowReceipt]   = useState(false);
  const [couponCode,    setCouponCode]    = useState('');
  const [couponData,    setCouponData]    = useState(null);
  const [couponLoading, setCouponLoading] = useState(false);
  const [showSummary,   setShowSummary]   = useState(false); // mobile cart toggle
  const [isMobile,      setIsMobile]      = useState(window.innerWidth < 768);
  const ITEMS_PER_PAGE = 10;
  const [visibleCount,  setVisibleCount]  = useState(10);
  const [loadingMore,   setLoadingMore]   = useState(false);
  const loaderRef = useRef(null);

  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);

  // Agar delivery disable ho aur user ne delivery select kar rakhi ho to reset karo
  useEffect(() => {
    if (!deliveryEnabled && form.order_type === 'delivery') {
      setForm(f => ({ ...f, order_type: 'dine-in' }));
    }
  }, [deliveryEnabled]);

  const handleApplyCoupon = async () => {
    if (!couponCode.trim()) return toast.error('Enter a coupon code');
    setCouponLoading(true);
    const pubApi = createPublicApi(slug);
    try {
      const res = await pubApi.coupons.validate({ code: couponCode.trim(), order_amount: total });
      setCouponData(res.data.data);
      toast.success('Coupon applied! You save ₹' + res.data.data.discount.toFixed(0));
    } catch (err) {
      setCouponData(null);
      toast.error(err.response?.data?.message || err.userMessage || 'Invalid coupon code');
    } finally { setCouponLoading(false); }
  };

  const removeCoupon = () => { setCouponData(null); setCouponCode(''); };

  useEffect(() => {
    if (!slug) return;
    const pubApi = createPublicApi(slug);
    pubApi.settings.get().then(r => {
      if(r.data.data) {
        setGstSettings(r.data.data);
        setDeliveryEnabled(r.data.data.delivery_enabled === 'true');
        setDeliveryCharge(parseFloat(r.data.data.delivery_charge) || 0);
      }
    }).catch(()=>{});
    pubApi.menu.getCategories().then(r => setCategories(r.data.data||[])).catch(()=>{});
    pubApi.menu.getAll({ limit:100 })
      .then(r => setMenuItems(r.data.data||[]))
      .catch(() => setMenuItems([]))
      .finally(() => setLoading(false));
  }, [slug]);

  const handleCheckout = async (e) => {
    e.preventDefault();
    if (!form.customer_name||!form.phone||!form.email) return toast.error('Name, phone and email are required');
    setSubmitting(true);
    try {
      const pubApi = createPublicApi(slug);
      const res = await pubApi.orders.create({
        ...form,
        items: items.map(i=>({ menu_item_id:i.id, quantity:i.quantity })),
        coupon_code: couponData?.code||null,
        discount_amount: discount,
        table_number: form.table_number || null,
      });
      setOrderId(res.data.data.order.order_number || res.data.data.order.id);
      setPlacedOrder({ ...res.data.data.order, items: res.data.data.items });
      clearCart(); setStep('success');
    } catch (err) { toast.error(err.response?.data?.message || err.userMessage || 'Failed to place order. Please try again.'); }
    finally { setSubmitting(false); }
  };

  const gstEnabled    = gstSettings.gst_enabled==='true' && parseFloat(gstSettings.gst_rate)>0;
  const gstRate       = parseFloat(gstSettings.gst_rate)||0;
  const discount      = couponData ? couponData.discount : 0;
  const afterDiscount = Math.max(total-discount, 0);
  const gstAmount     = gstEnabled ? (afterDiscount*gstRate)/100 : 0;
  const deliveryFee   = form.order_type === 'delivery' ? deliveryCharge : 0;
  const grandTotal    = afterDiscount+gstAmount+deliveryFee;

  const inp = {
    width:'100%', padding:'12px 16px', borderRadius:12, fontSize:14,
    border:`1.5px solid ${C.border}`, background:C.surface,
    color:C.text1, fontFamily:'inherit', outline:'none',
    boxSizing:'border-box', transition:'border-color 0.15s, box-shadow 0.15s',
  };
  const focusS = e => { e.target.style.borderColor=C.accent; e.target.style.boxShadow='0 0 0 3px rgba(201,100,40,0.1)'; };
  const blurS  = e => { e.target.style.borderColor=C.border; e.target.style.boxShadow='none'; };
  const Label  = ({children}) => (
    <label style={{ display:'block', color:C.text3, fontSize:12, fontWeight:700, marginBottom:8, textTransform:'uppercase', letterSpacing:0.5 }}>
      {children}
    </label>
  );

  const filteredMenu = activeCategory
    ? menuItems.filter(m => m.is_available && m.category_name === activeCategory)
    : menuItems.filter(m => m.is_available);
  const visibleMenu = filteredMenu.slice(0, visibleCount);
  const hasMore = visibleCount < filteredMenu.length;

  // Reset on category change
  useEffect(() => { setVisibleCount(10); }, [activeCategory]);

  // Infinite scroll observer
  const handleObserver = useCallback((entries) => {
    if (entries[0].isIntersecting && hasMore && !loadingMore && step === 'menu') {
      setLoadingMore(true);
      setTimeout(() => {
        setVisibleCount(prev => prev + 10);
        setLoadingMore(false);
      }, 400);
    }
  }, [hasMore, loadingMore, step]);

  useEffect(() => {
    const observer = new IntersectionObserver(handleObserver, { threshold: 0.1 });
    if (loaderRef.current) observer.observe(loaderRef.current);
    return () => observer.disconnect();
  }, [handleObserver]);

  // ── Order Summary Panel (shared) ──
  const OrderSummaryPanel = () => (
    <div style={{ background:C.card, borderRadius:20, border:`1px solid ${C.border}`, padding:24, boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
      <h3 style={{ fontFamily:'"Playfair Display",serif', fontSize:18, fontWeight:800, color:C.text1, margin:'0 0 18px', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
        Order Summary
        {isMobile && (
          <button onClick={()=>setShowSummary(false)}
            style={{ background:'none', border:'none', fontSize:20, cursor:'pointer', color:C.text4 }}>✕</button>
        )}
      </h3>

      {items.length===0 ? (
        <p style={{ color:C.text4, fontSize:14, textAlign:'center', padding:'12px 0' }}>No items added yet</p>
      ) : (
        <>
          <div style={{ display:'flex', flexDirection:'column', gap:8, marginBottom:16 }}>
            {items.map(i=>(
              <div key={i.id} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', fontSize:13 }}>
                <span style={{ color:C.text3, textTransform:'capitalize' }}>{i.name} × {i.quantity}</span>
                <span style={{ fontWeight:700, color:C.text1 }}>₹{(i.price*i.quantity).toFixed(0)}</span>
              </div>
            ))}
          </div>
          <div style={{ borderTop:`1px solid ${C.border2}`, paddingTop:14, display:'flex', flexDirection:'column', gap:8 }}>
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, color:C.text3 }}>
              <span>Subtotal</span><span>₹{total.toFixed(0)}</span>
            </div>
            {couponData && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, fontWeight:700, padding:'6px 10px', borderRadius:8, background:C.greenL, color:C.green }}>
                <span>🎟️ {couponData.code}</span><span>-₹{couponData.discount.toFixed(0)}</span>
              </div>
            )}
            {gstEnabled && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 10px', borderRadius:8, background:C.accentL, color:C.accent, fontWeight:600 }}>
                <span>{gstSettings.gst_label} @ {gstRate}%</span><span>+₹{gstAmount.toFixed(0)}</span>
              </div>
            )}
            {form.order_type === 'delivery' && (
              <div style={{ display:'flex', justifyContent:'space-between', fontSize:13, padding:'6px 10px', borderRadius:8, background:C.accentL, color:C.accent, fontWeight:600 }}>
                <span>🛵 Delivery Charge</span><span>{deliveryFee > 0 ? `+₹${deliveryFee.toFixed(0)}` : 'FREE'}</span>
              </div>
            )}
            <div style={{ display:'flex', justifyContent:'space-between', fontSize:17, fontWeight:800, paddingTop:10, borderTop:`2px solid ${C.border}`, color:C.text1 }}>
              <span>Total</span><span style={{ color:C.accent }}>₹{grandTotal.toFixed(0)}</span>
            </div>
          </div>
          <div style={{ marginTop:16, display:'flex', flexDirection:'column', gap:10 }}>
            {step==='menu' && (
              <button onClick={()=>{ setStep('cart'); setShowSummary(false); }}
                style={{ padding:'13px 0', borderRadius:12, border:'none', background:'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 3px 10px rgba(201,100,40,0.3)' }}>
                View Cart ({count} items)
              </button>
            )}
            {step==='cart' && (
              <button onClick={()=>{ setStep('checkout'); setShowSummary(false); }} disabled={items.length===0}
                style={{ padding:'13px 0', borderRadius:12, border:'none', background:items.length===0?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontSize:14, fontWeight:700, cursor:items.length===0?'not-allowed':'pointer', fontFamily:'inherit' }}>
                Proceed to Checkout →
              </button>
            )}
            {step==='checkout' && (
              <button onClick={()=>{ setStep('cart'); setShowSummary(false); }}
                style={{ padding:'12px 0', borderRadius:12, border:`1.5px solid ${C.border}`, background:C.card, color:C.text3, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                ← Back to Cart
              </button>
            )}
          </div>
        </>
      )}
    </div>
  );

  // ── Success ──
  if (step==='success') {
    return (
      <div style={{ minHeight:'100vh', paddingTop:120, display:'flex', alignItems:'center', justifyContent:'center', background:'#fdfaf7', padding:'120px 16px 60px' }}>
        <motion.div initial={{scale:0.8,opacity:0}} animate={{scale:1,opacity:1}}
          style={{ textAlign:'center', maxWidth:480, width:'100%' }}>
          <div style={{ width:90, height:90, borderRadius:'50%', background:C.greenL, border:'3px solid #a5d6a7', display:'flex', alignItems:'center', justifyContent:'center', fontSize:40, margin:'0 auto 20px', boxShadow:'0 8px 32px rgba(22,163,74,0.2)' }}>✅</div>
          <h2 style={{ fontFamily:'"Playfair Display",serif', fontSize:30, fontWeight:800, color:C.text1, margin:'0 0 10px' }}>Order Placed!</h2>
          <p style={{ color:C.text3, fontSize:16, margin:'0 0 6px' }}>Your order <strong style={{color:C.accent}}>#{orderId}</strong> has been received.</p>
          <p style={{ color:C.text4, fontSize:14, margin:'0 0 28px' }}>We'll start preparing it right away. Thank you!</p>
          <div style={{ display:'flex', flexDirection:'column', gap:12, maxWidth:280, margin:'0 auto' }}>
            <button onClick={()=>setShowReceipt(true)}
              style={{ padding:'14px 24px', borderRadius:13, border:'none', background:'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 16px rgba(201,100,40,0.35)' }}>
              📥 Download Receipt
            </button>
            <button onClick={()=>setStep('menu')}
              style={{ padding:'13px 24px', borderRadius:13, border:`1.5px solid ${C.border}`, background:C.card, color:C.text3, fontSize:14, fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
              Order More
            </button>
          </div>
        </motion.div>
        {showReceipt && placedOrder && <ReceiptGenerator order={placedOrder} slug={slug} onClose={()=>setShowReceipt(false)}/>}
      </div>
    );
  }

  return (
    <div style={{ minHeight:'100vh', paddingTop:96, paddingBottom: isMobile ? 100 : 80, background:'#fdfaf7' }}>
      <div style={{ maxWidth:1200, margin:'0 auto', padding:'0 16px' }}>

        {/* Header */}
        <div style={{ marginBottom:24 }}>
          <h1 style={{ fontFamily:'"Playfair Display",serif', fontSize: isMobile ? 26 : 36, fontWeight:800, color:C.text1, margin:'0 0 16px' }}>
            Order Online
          </h1>

          {/* Table QR Banner */}
          {tableFromUrl && (
            <div style={{ display:'inline-flex', alignItems:'center', gap:10, marginBottom:14, padding:'8px 16px', borderRadius:10, background:'#f0fdf4', border:'1.5px solid #a5d6a7' }}>
              <span style={{fontSize:18}}>🪑</span>
              <div>
                <p style={{color:'#15803d',fontWeight:800,fontSize:13,margin:0}}>Table {tableFromUrl}</p>
                <p style={{color:'#16a34a',fontSize:11,margin:0}}>Auto-detected from QR</p>
              </div>
            </div>
          )}

          {/* Steps */}
          <div style={{ display:'flex', alignItems:'center', gap:8, flexWrap:'nowrap', overflowX:'auto', paddingBottom:4, maxWidth:'100%' }}>
            <StepBadge num={1} label="Menu"     active={step==='menu'}     done={step==='cart'||step==='checkout'} onClick={()=>setStep('menu')}/>
            <span style={{ color:C.border, fontSize:16, flexShrink:0 }}>›</span>
            <StepBadge num={2} label="Cart"     active={step==='cart'}     done={step==='checkout'} onClick={()=>count>0&&setStep('cart')}/>
            <span style={{ color:C.border, fontSize:16, flexShrink:0 }}>›</span>
            <StepBadge num={3} label="Checkout" active={step==='checkout'} done={false} onClick={()=>count>0&&setStep('checkout')}/>
          </div>
        </div>

        {/* Main Layout */}
        <div style={{
          display:'grid',
          gridTemplateColumns: isMobile ? '1fr' : '1fr 340px',
          gap: isMobile ? 16 : 24,
          alignItems:'start',
        }}>

          {/* ── LEFT PANEL ── */}
          <div>
            <AnimatePresence mode="wait">

              {/* MENU */}
              {step==='menu' && (
                <motion.div key="menu" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}}>
                  {/* Category Filter */}
                  {categories.length > 0 && (
                    <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:16 }}>
                      <button onClick={()=>setActiveCategory('')}
                        style={{
                          padding:'6px 16px', borderRadius:99, fontSize:12, fontWeight:700,
                          border:`1.5px solid ${activeCategory===''?C.accent:C.border}`,
                          background:activeCategory===''?C.accent:C.card,
                          color:activeCategory===''?'white':C.text3,
                          cursor:'pointer', fontFamily:'inherit',
                        }}>All</button>
                      {categories.map(cat=>(
                        <button key={cat.id} onClick={()=>setActiveCategory(cat.name)}
                          style={{
                            padding:'6px 16px', borderRadius:99, fontSize:12, fontWeight:700,
                            border:`1.5px solid ${activeCategory===cat.name?C.accent:C.border}`,
                            background:activeCategory===cat.name?C.accent:C.card,
                            color:activeCategory===cat.name?'white':C.text3,
                            cursor:'pointer', fontFamily:'inherit',
                          }}>{cat.name}</button>
                      ))}
                    </div>
                  )}

                  <div style={{
                    display:'grid',
                    gridTemplateColumns: isMobile
                      ? 'repeat(2, 1fr)'
                      : 'repeat(auto-fill, minmax(240px, 1fr))',
                    gap: isMobile ? 12 : 16,
                  }}>
                    {loading
                      ? Array(6).fill(0).map((_,i)=><SkeletonCard key={i}/>)
                      : filteredMenu.length > 0
                        ? visibleMenu.map(item=><MenuItemCard key={item.id} item={item}/>)
                        : (
                          <div style={{ gridColumn:'1/-1', textAlign:'center', padding:'48px 0' }}>
                            <div style={{fontSize:48,marginBottom:12}}>☕</div>
                            <p style={{color:C.text4,fontSize:15}}>No items available</p>
                          </div>
                        )
                    }
                  </div>

                  {/* Count + Infinite scroll loader */}
                  {filteredMenu.length > 0 && (
                    <>
                      <p style={{ textAlign:'center', fontSize:12, color:C.text4, margin:'12px 0 4px' }}>
                        Showing {Math.min(visibleCount, filteredMenu.length)} of {filteredMenu.length} items
                      </p>
                      <div ref={loaderRef} style={{ height:40, display:'flex', alignItems:'center', justifyContent:'center' }}>
                        {loadingMore && (
                          <div style={{ display:'flex', gap:6 }}>
                            {[0,1,2].map(i => (
                              <div key={i} style={{ width:7, height:7, borderRadius:'50%', background:C.accent,
                                animation:`bounce 0.9s ${i*0.2}s infinite` }}/>
                            ))}
                          </div>
                        )}
                        {!hasMore && filteredMenu.length > 10 && (
                          <p style={{ fontSize:12, color:C.text4 }}>All items loaded ✓</p>
                        )}
                      </div>
                      <style>{`@keyframes bounce { 0%,100%{transform:scale(1);opacity:0.4} 50%{transform:scale(1.4);opacity:1} }`}</style>
                    </>
                  )}
                </motion.div>
              )}

              {/* CART */}
              {step==='cart' && (
                <motion.div key="cart" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}}
                  style={{ background:C.card, borderRadius:20, border:`1px solid ${C.border}`, padding: isMobile?18:28, boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
                  <h2 style={{ fontFamily:'"Playfair Display",serif', fontSize:22, fontWeight:800, color:C.text1, margin:'0 0 20px' }}>Your Cart</h2>
                  {items.length===0 ? (
                    <div style={{ textAlign:'center', padding:'48px 0' }}>
                      <div style={{fontSize:48,marginBottom:12}}>🛒</div>
                      <p style={{color:C.text4,fontSize:15,margin:'0 0 20px'}}>Your cart is empty</p>
                      <button onClick={()=>setStep('menu')}
                        style={{ padding:'12px 24px', borderRadius:12, border:'none', background:'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontSize:14, fontWeight:700, cursor:'pointer', fontFamily:'inherit' }}>
                        Browse Menu
                      </button>
                    </div>
                  ) : (
                    <div style={{ display:'flex', flexDirection:'column', gap:10 }}>
                      {items.map(item=>(
                        <div key={item.id} style={{ display:'flex', alignItems:'center', gap: isMobile?8:12, padding: isMobile?'10px 10px':'12px 14px', borderRadius:14, border:`1px solid ${C.border2}`, background:C.surface }}>
                          <img src={item.image_url?(item.image_url.startsWith('http')?item.image_url:`${BASE}${item.image_url}`):PLACEHOLDER} alt={item.name}
                            style={{ width: isMobile?48:60, height: isMobile?48:60, borderRadius:10, objectFit:'cover', flexShrink:0 }}
                            onError={e=>{ e.target.src=PLACEHOLDER; }}/>
                          <div style={{ flex:1, minWidth:0 }}>
                            <p style={{ color:C.text1, fontWeight:700, fontSize: isMobile?13:14, margin:0, textTransform:'capitalize', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap', maxWidth:'calc(100% - 10px)' }}>{item.name.length > 10 ? item.name.substring(0, 10) + '...' : item.name}</p>
                            <p style={{ color:C.accent, fontWeight:800, fontSize:14, margin:'3px 0 0' }}>₹{(item.price*item.quantity).toFixed(0)}</p>
                          </div>
                          <div style={{ display:'flex', alignItems:'center', gap:6, flexShrink:0 }}>
                            <button onClick={()=>updateQty(item.id,item.quantity-1)}
                              style={{ width:28, height:28, borderRadius:'50%', border:`1px solid ${C.border}`, background:C.card, cursor:'pointer', fontSize:15, fontWeight:700, color:C.text3, display:'flex', alignItems:'center', justifyContent:'center' }}>−</button>
                            <span style={{ width:22, textAlign:'center', fontWeight:700, color:C.text1, fontSize:14 }}>{item.quantity}</span>
                            <button onClick={()=>updateQty(item.id,item.quantity+1)}
                              style={{ width:28, height:28, borderRadius:'50%', border:'none', background:C.accent, cursor:'pointer', fontSize:15, fontWeight:700, color:'#fff', display:'flex', alignItems:'center', justifyContent:'center' }}>+</button>
                          </div>
                          <button onClick={()=>removeItem(item.id)}
                            style={{ background:'transparent', border:'none', cursor:'pointer', color:'#ef4444', fontSize:16, padding:4, flexShrink:0 }}>✕</button>
                        </div>
                      ))}
                      <button onClick={()=>setStep('checkout')}
                        style={{ marginTop:8, padding:'14px 0', borderRadius:13, border:'none', background:'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontSize:15, fontWeight:700, cursor:'pointer', fontFamily:'inherit', boxShadow:'0 4px 14px rgba(201,100,40,0.3)' }}>
                        Proceed to Checkout →
                      </button>
                    </div>
                  )}
                </motion.div>
              )}

              {/* CHECKOUT */}
              {step==='checkout' && (
                <motion.div key="checkout" initial={{opacity:0,y:12}} animate={{opacity:1,y:0}} exit={{opacity:0,y:-12}}
                  style={{ background:C.card, borderRadius:20, border:`1px solid ${C.border}`, padding: isMobile?18:28, boxShadow:'0 2px 12px rgba(0,0,0,0.05)' }}>
                  <h2 style={{ fontFamily:'"Playfair Display",serif', fontSize:22, fontWeight:800, color:C.text1, margin:'0 0 24px' }}>Your Details</h2>
                  <form onSubmit={handleCheckout} style={{ display:'flex', flexDirection:'column', gap:16 }}>

                    <div style={{ display:'grid', gridTemplateColumns: isMobile?'1fr':'1fr 1fr', gap:14 }}>
                      <div>
                        <Label>Full Name *</Label>
                        <input style={inp} placeholder="John Smith" required value={form.customer_name}
                          onChange={e=>setForm(f=>({...f,customer_name:e.target.value}))} onFocus={focusS} onBlur={blurS}/>
                      </div>
                      <div>
                        <Label>Phone *</Label>
                        <input style={inp} placeholder="+91 98765 43210" required value={form.phone}
                          onChange={e=>setForm(f=>({...f,phone:e.target.value}))} onFocus={focusS} onBlur={blurS}/>
                      </div>
                    </div>

                    <div>
                      <Label>Email *</Label>
                      <input type="email" style={inp} placeholder="john@example.com" required value={form.email}
                        onChange={e=>setForm(f=>({...f,email:e.target.value}))} onFocus={focusS} onBlur={blurS}/>
                    </div>

                    <div>
                      <Label>Order Type</Label>
                      <select style={inp} value={form.order_type} onChange={e=>setForm(f=>({...f,order_type:e.target.value}))} onFocus={focusS} onBlur={blurS}>
                        <option value="dine-in">🍽️ Dine In</option>
                        <option value="takeaway">🥡 Takeaway</option>
                        {deliveryEnabled && (
                          <option value="delivery">🛵 Delivery</option>
                        )}
                      </select>
                    </div>

                    {form.order_type==='dine-in' && (
                      <div>
                        <Label>Table Number {tableFromUrl && <span style={{color:'#22c55e',fontSize:11,fontWeight:600,marginLeft:6}}>✓ Auto-filled</span>}</Label>
                        <input style={{...inp, background:tableFromUrl?'#f0fdf4':undefined, borderColor:tableFromUrl?'#a5d6a7':undefined}}
                          placeholder="e.g. 5, A2" value={form.table_number}
                          onChange={e=>setForm(f=>({...f,table_number:e.target.value}))} onFocus={focusS} onBlur={blurS}/>
                      </div>
                    )}

                    {form.order_type==='delivery' && (
                      <div>
                        <Label>Delivery Address *</Label>
                        <input style={inp} placeholder="123 Main St, City" value={form.address}
                          onChange={e=>setForm(f=>({...f,address:e.target.value}))} onFocus={focusS} onBlur={blurS}/>
                      </div>
                    )}

                    {/* Coupon */}
                    <div>
                      <Label>🎟️ Coupon Code</Label>
                      {couponData ? (
                        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 16px', borderRadius:12, background:C.greenL, border:'1.5px solid #a5d6a7', flexWrap:'wrap', gap:8 }}>
                          <div>
                            <p style={{ color:C.green, fontWeight:800, fontSize:14, margin:0, fontFamily:'monospace', letterSpacing:1 }}>{couponData.code}</p>
                            <p style={{ color:'#15803d', fontSize:12, margin:'2px 0 0' }}>You save ₹{couponData.discount.toFixed(0)}! 🎉</p>
                          </div>
                          <button type="button" onClick={removeCoupon}
                            style={{ background:'transparent', border:'1.5px solid #a5d6a7', borderRadius:8, padding:'5px 12px', color:C.green, cursor:'pointer', fontSize:12, fontWeight:700 }}>
                            Remove
                          </button>
                        </div>
                      ) : (
                        <div style={{ display:'flex', gap:10 }}>
                          <input style={{ ...inp, flex:1, fontFamily:'monospace', fontWeight:700, letterSpacing:1 }}
                            placeholder="WELCOME10" value={couponCode}
                            onChange={e=>setCouponCode(e.target.value.toUpperCase())}
                            onKeyDown={e=>e.key==='Enter'&&(e.preventDefault(),handleApplyCoupon())}
                            onFocus={focusS} onBlur={blurS}/>
                          <button type="button" onClick={handleApplyCoupon} disabled={couponLoading}
                            style={{ padding:'12px 18px', borderRadius:12, border:'none', background:couponLoading?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontWeight:700, cursor:couponLoading?'not-allowed':'pointer', fontSize:13, fontFamily:'inherit', whiteSpace:'nowrap' }}>
                            {couponLoading?'⏳':'Apply'}
                          </button>
                        </div>
                      )}
                    </div>

                    {/* Payment Info Card */}
                    <div style={{ display:'flex', alignItems:'center', gap:14, padding:'14px 16px', borderRadius:14, border:'1.5px solid #e8d5c4', background:'#fff7f2' }}>
                      <div style={{ width:42, height:42, borderRadius:12, background:'linear-gradient(135deg,#c96428,#9a4a1e)', display:'flex', alignItems:'center', justifyContent:'center', fontSize:20, flexShrink:0 }}>
                        {form.order_type==='dine-in' ? '🍽️' : form.order_type==='takeaway' ? '🥡' : '💵'}
                      </div>
                      <div>
                        <p style={{ margin:0, fontWeight:700, fontSize:14, color:'#0f172a' }}>
                          {form.order_type==='dine-in' ? 'Pay at Counter' : form.order_type==='takeaway' ? 'Pay at Pickup' : 'Cash on Delivery'}
                        </p>
                        <p style={{ margin:'2px 0 0', fontSize:12, color:'#64748b' }}>
                          {form.order_type==='dine-in'
                            ? 'Enjoy your meal and pay at the counter after dining.'
                            : form.order_type==='takeaway'
                            ? 'Pay at the counter when you pick up your order.'
                            : 'Pay in cash when your order arrives at your door.'}
                        </p>
                      </div>
                      <div style={{ marginLeft:'auto', width:20, height:20, borderRadius:'50%', border:'2px solid #c96428', background:'#c96428', display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                        <div style={{ width:8, height:8, borderRadius:'50%', background:'#fff' }}/>
                      </div>
                    </div>

                    <div>
                      <Label>Special Instructions</Label>
                      <textarea style={{ ...inp, resize:'none', lineHeight:1.6, height:80 }}
                        placeholder="Any dietary requirements or special requests..."
                        value={form.notes} onChange={e=>setForm(f=>({...f,notes:e.target.value}))} onFocus={focusS} onBlur={blurS}/>
                    </div>

                    <motion.button whileTap={{scale:0.98}} type="submit" disabled={submitting}
                      style={{ padding:'15px 0', borderRadius:13, border:'none', background:submitting?'#e8d5c4':'linear-gradient(135deg,#c96428,#9a4a1e)', color:'#fff', fontSize:16, fontWeight:800, cursor:submitting?'not-allowed':'pointer', fontFamily:'inherit', boxShadow:submitting?'none':'0 4px 18px rgba(201,100,40,0.35)' }}>
                      {submitting?'⏳ Placing Order...':`Place Order · ₹${grandTotal.toFixed(0)}`}
                    </motion.button>
                  </form>
                </motion.div>
              )}
            </AnimatePresence>
          </div>

          {/* ── RIGHT: Order Summary (desktop only) ── */}
          {!isMobile && (
            <div style={{ position:'sticky', top:100 }}>
              <OrderSummaryPanel/>
            </div>
          )}

          {/* ── MOBILE: Inline Summary on cart/checkout step ── */}
          {isMobile && (step==='cart' || step==='checkout') && (
            <div style={{ marginTop: 16 }}>
              <OrderSummaryPanel/>
            </div>
          )}
        </div>
      </div>

      {/* ── MOBILE: Floating Cart Button ── */}
      {isMobile && step==='menu' && count > 0 && (
        <motion.div
          initial={{ y:100, opacity:0 }} animate={{ y:0, opacity:1 }}
          style={{
            position:'fixed', bottom:20, left:16, right:16, zIndex:100,
          }}>
          <button onClick={()=>setStep('cart')}
            style={{
              width:'100%', padding:'16px 20px', borderRadius:16, border:'none',
              background:'linear-gradient(135deg,#c96428,#9a4a1e)',
              color:'white', fontSize:15, fontWeight:800, cursor:'pointer',
              fontFamily:'inherit', boxShadow:'0 8px 32px rgba(201,100,40,0.5)',
              display:'flex', alignItems:'center', justifyContent:'space-between',
            }}>
            <div style={{ display:'flex', alignItems:'center', gap:10 }}>
              <span style={{ background:'rgba(255,255,255,0.25)', borderRadius:99, width:28, height:28, display:'flex', alignItems:'center', justifyContent:'center', fontSize:13, fontWeight:800 }}>
                {count}
              </span>
              <span>View Cart</span>
            </div>
            <span style={{ fontWeight:800 }}>₹{grandTotal.toFixed(0)} →</span>
          </button>
        </motion.div>
      )}

      {/* ── MOBILE: Summary Sheet ── */}
      <AnimatePresence>
        {isMobile && showSummary && (
          <>
            <motion.div initial={{opacity:0}} animate={{opacity:1}} exit={{opacity:0}}
              onClick={()=>setShowSummary(false)}
              style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.5)', zIndex:200 }}/>
            <motion.div
              initial={{y:'100%'}} animate={{y:0}} exit={{y:'100%'}}
              transition={{ type:'spring', damping:30, stiffness:300 }}
              style={{
                position:'fixed', bottom:0, left:0, right:0, zIndex:201,
                background:C.card, borderRadius:'24px 24px 0 0',
                padding:24, maxHeight:'80vh', overflowY:'auto',
                boxShadow:'0 -8px 40px rgba(0,0,0,0.2)',
              }}>
              <OrderSummaryPanel/>
            </motion.div>
          </>
        )}
      </AnimatePresence>
    </div>
  );
};

export default Order;
